import * as React from 'react'
import { styled } from '@mui/material/styles'
import MuiDrawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
// import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
// import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
// import Badge from '@mui/material/Badge';
// import Container from '@mui/material/Container';
// import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
// import NotificationsIcon from '@mui/icons-material/Notifications';
import { useLocation, useNavigationType } from 'react-router-dom'
import { useDesktop } from 'components/core/SplitPanes'
import { MainListItems, SecondaryListItems, useNoteRecent } from './NavItems'
import TabBar, { TAB_BAR_SPACE } from './TabBar'

const drawerWidth = 240

// The pages a phone scrolls as a whole, so Safari's toolbar minimizes as the
// reader scrolls: the tab bar's pages, More, and Cost, a long page sized to
// nothing. The rest keep the desktop's frame, which fills the window and
// scrolls inside, since their maps, graphs and panes are sized to it. A page
// here gives its root `width: '100%'`: `main` lays its child out as a flex
// item, which otherwise grows to its widest content (a wide table) and runs
// past the screen.
const PAGE_SCROLLED = [
  '/chat',
  '/users',
  '/moments',
  '/reports',
  '/more',
  '/cost',
]

// Where each history entry was scrolled to, for back and forward between the
// pages a phone scrolls as a whole. The browser's own restoring stays on:
// Safari's back swipe shows its picture of the page only while it restores
// the place itself (with it off, only when the page hasn't scrolled since).
// It restores before a page that reads its rows has them, though, and lands
// short; this lands it.
const places = new Map()

// A new page opens at its top. Back or forward to a page returns to its
// place once the page has grown to hold it, as a list does when its rows
// land, unless the reader scrolls first. A step within a page (a thread or
// an item opening) is the page's own.
const usePagePlaces = () => {
  const location = useLocation()
  const navigationType = useNavigationType()
  const path = React.useRef(location.pathname)

  React.useEffect(() => {
    const note = () => places.set(location.key, window.scrollY)
    window.addEventListener('scroll', note, { passive: true })
    return () => window.removeEventListener('scroll', note)
  }, [location.key])

  React.useEffect(() => {
    if (location.pathname === path.current) return undefined
    path.current = location.pathname
    const place = navigationType === 'POP' ? places.get(location.key) || 0 : 0
    window.scrollTo(0, place)
    if (!place) return undefined
    const until = Date.now() + 3000
    let frame = requestAnimationFrame(function settle() {
      if (Math.abs(window.scrollY - place) < 2 || Date.now() > until) return
      window.scrollTo(0, place)
      frame = requestAnimationFrame(settle)
    })
    const stop = () => cancelAnimationFrame(frame)
    window.addEventListener('touchstart', stop, { passive: true })
    window.addEventListener('wheel', stop, { passive: true })
    return () => {
      stop()
      window.removeEventListener('touchstart', stop)
      window.removeEventListener('wheel', stop)
    }
  }, [location, navigationType])
}

// const AppBar = styled(MuiAppBar, {
//   shouldForwardProp: (prop) => prop !== 'open',
// })(({ theme, open }) => ({
//   zIndex: theme.zIndex.drawer + 1,
//   transition: theme.transitions.create(['width', 'margin'], {
//     easing: theme.transitions.easing.sharp,
//     duration: theme.transitions.duration.leavingScreen,
//   }),
//   ...(open && {
//     marginLeft: drawerWidth,
//     width: `calc(100% - ${drawerWidth}px)`,
//     transition: theme.transitions.create(['width', 'margin'], {
//       easing: theme.transitions.easing.sharp,
//       duration: theme.transitions.duration.enteringScreen,
//     }),
//   }),
// }));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    // Pin the drawer to the viewport so the nav list scrolls internally
    // (instead of growing the page) and the bottom section stays put. The
    // dynamic height, since iPhone Safari's 100vh runs on under its toolbar.
    height: '100dvh',
    overflow: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9),
      },
    }),
  },
}))

export default function DashboardContent({ children }) {
  const [open, setOpen] = React.useState(false)
  // Wallboard mode: fullscreen (e.g. the Heartbeats page's toggle) hides
  // the nav entirely so the content gets the whole display.
  const [fullscreen, setFullscreen] = React.useState(false)
  const toggleDrawer = () => {
    setOpen(!open)
  }

  React.useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  // A phone has the tab bar in place of the drawer, and room kept for it
  // (and, in a Home Screen web app, for the home indicator) under the page.
  const desktop = useDesktop()
  const { pathname } = useLocation()
  const tabBar = !desktop && !fullscreen
  usePagePlaces()
  useNoteRecent()
  const tabBarSpace = tabBar ? `${TAB_BAR_SPACE}px` : '0px'
  const mainSx = desktop
    ? { height: '100dvh', overflowY: 'auto', overflowX: 'hidden' }
    : PAGE_SCROLLED.some((path) => pathname.startsWith(path))
    ? { minHeight: '100dvh', pb: tabBarSpace, overflowX: 'clip' }
    : {
        height: `calc(100dvh - ${tabBarSpace})`,
        overflowY: 'auto',
        overflowX: 'hidden',
      }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* <AppBar position="absolute" open={open}>
        <Toolbar
          sx={{
            pr: '24px', // keep right padding when drawer closed
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: '36px',
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            Dashboard
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={4} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar> */}
      {desktop && (
        <Drawer
          variant="permanent"
          open={open}
          sx={fullscreen ? { display: 'none' } : undefined}
        >
          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: [1],
            }}
          >
            <IconButton onClick={toggleDrawer}>
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Toolbar>
          <Divider />
          <List
            component="nav"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <MainListItems />
          </List>
          <Box sx={{ flex: 0, paddingBottom: 1 }}>
            <SecondaryListItems />
          </Box>
        </Drawer>
      )}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          position: 'relative',
          ...mainSx,
        }}
      >
        {/* <Toolbar /> */}
        {/* <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}> */}
        {children}
        {/* </Container> */}
      </Box>
      {tabBar && <TabBar />}
    </Box>
  )
}
